import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { OnlineStatusService } from 'ngx-online-status';
import { BehaviorSubject, debounceTime, fromEvent, Observable, Subscription } from 'rxjs';
import { locationsData } from 'src/app/data/location.data';
import { EntityData } from 'src/app/models/entity-app.model';
import { HighlightPropertyModel } from 'src/app/models/highlights-property.model';
import { EventService } from 'src/app/services/event.service';
import { LoadingService } from 'src/app/services/loading.service';
import { MetaService } from 'src/app/services/meta.service';
import { PropertyService } from 'src/app/services/property.service';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {

  isMobile: BehaviorSubject<boolean> = new BehaviorSubject(true);
  highlightsProperties!: HighlightPropertyModel[];
  localAccommodations!: HighlightPropertyModel[];
  popularCities: any[] = [];
  searchData: any;
  resizeObservable$!: Observable<Event>;
  resizeSubscription$!: Subscription;
  latestSearches: any[] = [];
  subscriptions: Subscription = new Subscription;
  networkStatus: number = 1;
  requests: number = 0;
  entityData!: EntityData;

  constructor(
    private eventService: EventService,
    public loadingService: LoadingService,
    private dbService: NgxIndexedDBService,
    private propertyService: PropertyService,
    private onlineService: OnlineStatusService,
    private metaService: MetaService
  ) { }

  ngOnInit(): void {
    this.metaService.changeMeta('HOME');
    const entity: any = localStorage.getItem('entityData');
    this.entityData = JSON.parse(entity);
    // this.loadingService.setLoading(true);
    this.loadingService.setPlaceholder(true);
    this.resizeObservable$ = fromEvent(window, 'resize');
    this.resizeSubscription$ = this.resizeObservable$.pipe(debounceTime(100)).subscribe((evt: any) => {
      if (evt?.isTrusted) {
        this.onResize(evt.target);
      } else {
        this.onResize();
      }
    });
    this.networkStatus = this.onlineService.getStatus();
    this.onlineService.status.subscribe(status => this.networkStatus = status);
    this.loadCss();
    this.onResize();

    this.initHome();

    this.eventService.broadcast('header-page-title', '');
    const languageSubscription = this.eventService.subscribe('language-change', () => {
      this.initHome();
    });
    this.subscriptions.add(languageSubscription);
  }

  ngAfterViewInit(): void {
    const interval = setInterval(() => {
      if (this.requests > 1) {
        this.loadingService.setPlaceholder(false);
        clearInterval(interval);
      }
    });
  }

  initHome() {
    const propertySubscription = this.propertyService.getHighlightsProperties().subscribe({
      next: (data: any) => {
        this.highlightsProperties = data;
        this.savePropertiesIndexedDb(data);
        this.loadingService.setLoading(false);
        this.requests++;
      },
      error: (err: any) => {
        this.dbService.getAll('highlights-properties').subscribe((properties: any[]) => this.highlightsProperties = properties);
        this.loadingService.setLoading(false);
        this.requests++;
      }
    });
    const dataSubscription = this.propertyService.getDataToSearch().subscribe(
      {
        next: data => {
          this.searchData = data;
          this.requests++;
          localStorage.setItem('searchData', JSON.stringify(data));
        },
        error: err => {
          const data = localStorage.getItem('searchData');
          this.requests++;
          if (data)
            this.searchData = JSON.parse(data);
        }
      });
    const latestSubscription = this.dbService.getAll('latest-searches').subscribe({
      next: (latest: any[]) => {
        this.requests++;
        if (latest?.length > 0) {
          this.latestSearches = latest;
        }
      },
      error: (err: string) => {
        this.requests++;
        if (err.startsWith('objectStore does not exists')) {
          this.dbService.createObjectStore({
            store: 'latest-searches', storeConfig: { keyPath: 'searchKey', autoIncrement: false },
            storeSchema: []
          });
        }
      }
    });
    if (this.entityData.localAccommodation && this.entityData.localAccommodation === true) {
      const localSubscription = this.propertyService.getLocalAccommodations().subscribe({
        next: (data: any) => {
          this.localAccommodations = data;
          this.loadingService.setLoading(false);
        },
        error: (err: any) => {
          this.loadingService.setLoading(false);
        }
      });
      this.subscriptions.add(localSubscription);
    }
    this.subscriptions.add(propertySubscription);
    this.subscriptions.add(dataSubscription);
    this.subscriptions.add(latestSubscription);
  }

  savePropertiesIndexedDb(properties: any[]) {
    this.dbService.clear('highlights-properties').subscribe();
    properties.forEach(property => {
      this.dbService.update('highlights-properties', property).subscribe(
        {
          error: (err: string) => {
            console.log(err);
            if (err.startsWith('objectStore does not exists')) {
              this.dbService.createObjectStore({
                store: 'highlights-properties', storeConfig: { keyPath: 'uniqueId', autoIncrement: false },
                storeSchema: []
              });
            }
          }
        }
      );
      this.dbService.update('searched-properties', property).subscribe(
        {
          error: (err: string) => {
            if (err.startsWith('objectStore does not exists')) {
              this.dbService.createObjectStore({
                store: 'searched-properties', storeConfig: { keyPath: 'uniqueId', autoIncrement: false },
                storeSchema: []
              });
            }
          }
        }
      );
    });
  }

  onResize(event: any = null) {
    if (event === null) {
      window.innerWidth > 1024 ? this.isMobile.next(false) : this.isMobile.next(true);
    } else {
      event.outerWidth < 1024 || event?.screen?.orientation?.angle > 0 ? this.isMobile.next(true) : this.isMobile.next(false);
    }
  }

  loadCss() {
    const dynamicCss = [
      "assets/css/animate.css",
      "assets/css/menu.css",
      "assets/css/maps.css"
    ];

    for (let i = 0; i < dynamicCss.length; i++) {
      const node = document.createElement('link');
      node.rel = "stylesheet";
      node.href = dynamicCss[i];
      document.getElementsByTagName('head')[0].appendChild(node);
    }
  }

  ngOnDestroy(): void {
    this.resizeSubscription$.unsubscribe();
    this.subscriptions.unsubscribe();
  }

}
